import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { SkeletonTheme } from 'react-loading-skeleton';
import { useLocation, useMatch, useNavigate } from 'react-router';

import { ModalSize } from 'components/modal';
import { ModalQuestUnlockedIncentive } from 'components/modal-quest-unlocked-incentive';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useEnableGreyedOutOffers } from 'hooks/use-enable-greyed-out-offers';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useLoyaltyExternalOffers } from 'hooks/use-loyalty-external-offers';
import { useLoyaltyExternalOffersEmptyStates } from 'hooks/use-loyalty-external-offers-empty-states';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { useStoreCardButtonOptions } from 'hooks/use-store-card-button-options';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames } from 'state/cdp/constants';
import { selectors, useAppSelector } from 'state/global-state';
import { useFlag } from 'state/launchdarkly';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';
import { useLoyaltyQuests } from 'state/loyalty/hooks/use-loyalty-quests';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { ExternalOffer } from 'state/loyalty/types';
import { isTestOnly, isUpcomingOffer } from 'state/loyalty/utils';
import { ServiceModeWithCatering } from 'state/service-mode/types';
import { useStoreContext } from 'state/store';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { routes } from 'utils/routing';

import { parseExternalIncentiveData } from '../loyalty-incentives-components/external-incentive-card/parse-external-incentive-data';
import { ExternalIncentiveDetailsMemo } from '../loyalty-incentives-components/external-incentive-details';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { IncentiveDetailsMemo } from '../loyalty-incentives-components/incentive-details';

import { EmptyOffersView } from './loyalty-offers-v2/EmptyOffersView';
import { ExternalOfferCardV2, OfferCardV2 } from './loyalty-offers-v2/loyalty-offers-card-v2';
import { OffersCardV2Skeleton } from './loyalty-offers-v2/loyalty-offers-card-v2-skeleton';
import {
  AddPromoIcon,
  FilterBarWrapper,
  FilterContainer,
  LoyaltyOffersGridContainer,
  OfferGrid,
  OfferStickyContainer,
  OfferTitle,
  OfferTitleContainer,
  PromoCodeButtonBar,
  PromoCodeButtonTitle,
  PromoCodeCTA,
  StyledButton,
  StyledExternalModal,
  StyledV2Modal,
} from './loyalty-offers-v2/loyalty-offers-grid.styles';
import { LoyaltyPromoCode } from './loyalty-promo-code';
import { AvailableTabsType } from './types';
import { useOfferPrice } from './useOfferPrice';
import { useOfferView } from './useOfferView';
import { isExternalOfferClosed, isGreyedOutOffer } from './utils';

type TLocationState = { selectedTab?: AvailableTabsType; backRoute?: string } | undefined;

export const OfferViewV2 = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const state = location.state as TLocationState;
  const { user } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { isFavorite, toggleFavorite, isOfferLoading } = useFavoriteOffers();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);
  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);
  const showRewardCarousel = useFlag(LaunchDarklyFlag.ENABLE_REWARD_CAROUSEL_IN_OFFERS_PAGE);
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const { isAuthenticated } = useAuthContext();
  const { enableInvitationCode } = useFeatureInvitationCode();
  const { externalOffersEmptyStates: enableExternalOffersEmptyState } =
    useLoyaltyExternalOffersEmptyStates();
  const { externalOffers: originalExternalOffers, externalOffersLoading } =
    useLoyaltyExternalOffers();
  const userIsAuthenticated = isAuthenticated();
  const showFilters = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_GRID_VIEW_WITH_FILTER);
  const deliveryOnlyFilter = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const showDeliveryFilter = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);

  const cdp = useCdpContext();
  const { restaurantLogAttributesRef, store } = useStoreContext();
  const storeOrderFlag = useStoreCardButtonOptions();

  const [cardWidth, setCardWidth] = useState(166); // default to mobile
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateCardWidth = () => {
      const viewportWidth = ref.current?.offsetWidth ?? 0;
      const idealWidth = isMobile ? 166 : 172;
      const gap = 20;

      const cardsPerRow = Math.floor((viewportWidth + gap) / (idealWidth + gap));
      const totalGap = (cardsPerRow - 1) * gap;
      const availableWidth = viewportWidth - totalGap;
      const scaledCardWidth = Math.floor(availableWidth / cardsPerRow);

      setCardWidth(scaledCardWidth);
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, [isMobile]);

  const enableExternalOffers =
    originalExternalOffers || enableExternalOffersEmptyState ? true : false;
  const availableFilters = [
    {
      id: 0,
      name: 'allOffers',
      label: formatMessage({ id: 'allOffers' }),
      shouldExist: true,
    },
    {
      id: 1,
      name: ' personalOffers',
      label: formatMessage({ id: 'forYou' }),
      shouldExist: true,
    },
    {
      id: 2,
      name: ' favoriteOffers',
      label: formatMessage({ id: 'favorite' }),
      shouldExist: true,
    },
    {
      id: 3,
      name: ' deliveryOffers',
      label: formatMessage({ id: 'delivery' }),
      shouldExist:
        showDeliveryFilter &&
        !deliveryOnlyFilter &&
        (storeOrderFlag.includes(StoreCardButtonOptions.ORDER) ||
          storeOrderFlag.includes(StoreCardButtonOptions.OFFERS)),
    },
    {
      id: 4,
      name: ' pickupOffers',
      label: formatMessage({ id: 'pickUp' }),
      shouldExist:
        showDeliveryFilter &&
        !deliveryOnlyFilter &&
        (storeOrderFlag.includes(StoreCardButtonOptions.ORDER) ||
          storeOrderFlag.includes(StoreCardButtonOptions.OFFERS)),
    },
    {
      id: 5,
      name: ' externalOffers',
      label: formatMessage({ id: 'offersExternal' }),
      shouldExist: enableExternalOffers,
    },
  ];
  const tabNames = ['All Offers', 'For You', 'Favorites', 'Delivery', 'Pickup', 'External Offers'];

  const [activeFilter, setActiveFilter] = React.useState(0);

  const { enableGreyedOutOffers } = useEnableGreyedOutOffers(userIsAuthenticated);
  const navigate = useNavigate();

  const { params } =
    useMatch({
      path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*`,
    }) || {};

  const loyaltyEngineId = params?.['*'];
  const tabPath = params?.tab! || '';

  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();

  const {
    challengeIncentives,
    onChallengeIcentiveSelect,
    challengeIncentiveSelected,
    questsLoading,
    onChangeUnlockedIncentivesModalVisibilityStatus,
    unlockedIncentivesModalVisibilityStatus,
  } = useLoyaltyQuests(loyaltyUser?.id ?? '');

  const externalOffers = originalExternalOffers as ExternalOffer[];

  const { onPromoCodeRedeemSuccess, offers, selectedIncentive, handleClick } = useOfferView({
    enableLoyaltyPromoCodes,
    loyaltyUser,
    user,
    appliedOffers,
    inRestaurantRedemptionCart,
    loyaltyEngineId,
    enableGeolocation,
    offersLoading,
    tabPath,
    externalOffers,
    challengeIncentives,
    isMobile: true,
    onChallengeIcentiveSelect,
  });

  const isAvailableForDeliveryAndPickup = (filterId: number) => {
    if (store?._id) {
      if (filterId === 3) {
        // Delivery
        return store?.hasDelivery;
      } else if (filterId === 4) {
        // Pickup
        return (
          store?.hasDriveThru ||
          store?.hasTakeOut ||
          store?.hasTableService ||
          store?.hasCurbside ||
          store?.hasDineIn ||
          store?.hasCatering
        );
      }
    }
    return showDeliveryFilter && !deliveryOnlyFilter;
  };

  const { shouldShowOfferCardPrice, getOfferCardPrice } = useOfferPrice();

  const isExternalOffer = selectedIncentive?.__typename === 'ExternalOffers';

  const upsellOffers = offers?.reduce((acc: string[], offer) => {
    if (offer.upsellOptions?.length) {
      offer.upsellOptions.forEach(upsellOffer => acc.push(upsellOffer?.loyaltyEngineId!));
    }
    return acc;
  }, []);

  const offersAndTiles = offers
    .filter((offerOrTile: any) => {
      // discard offer types except systemWideOffer and configOffer for "All Offers" tab
      if (
        activeFilter === 0 &&
        offerOrTile?._type !== 'configOffer' &&
        offerOrTile?._type !== 'systemwideOffer'
      ) {
        return false;
      }

      // discard external offers for "External/Benefits" tab
      if (activeFilter === 5) {
        return false;
      }

      // discard offer types except configOffer for "For You" tab
      if (activeFilter === 1 && offerOrTile?._type !== 'configOffer') {
        return false;
      }

      // discard offers that are not favourited for "Favourites tab"
      if (activeFilter === 2 && !isFavorite(offerOrTile._id || '')) {
        return false;
      }

      // discard offers which has not delivery service mode for "Delivery" tab
      if (activeFilter === 3) {
        return (
          offerOrTile.serviceModes?.includes(ServiceModeWithCatering.DELIVERY) &&
          isAvailableForDeliveryAndPickup(3)
        );
      }

      // discard offers which has not pickup service mode for "Pickup" tab
      if (activeFilter === 4) {
        return (
          offerOrTile.serviceModes?.some((mode: ServiceModeWithCatering) =>
            [
              ServiceModeWithCatering.CURBSIDE,
              ServiceModeWithCatering.DRIVE_THRU,
              ServiceModeWithCatering.TAKEOUT,
              ServiceModeWithCatering.TABLE_SERVICE,
              ServiceModeWithCatering.EAT_IN,
              ServiceModeWithCatering.CATERING_PICKUP,
            ].includes(mode)
          ) && isAvailableForDeliveryAndPickup(4)
        );
      }

      return true;
    })
    .map((offerOrTile: any, idx: number) => {
      const offerIsUpcoming = isUpcomingOffer(offerOrTile);

      if (upsellOffers.includes(offerOrTile?.loyaltyEngineId) || isTestOnly(offerOrTile)) {
        return null;
      }

      const incentiveData = parseIncentiveData(offerOrTile);

      const { priceToShow, slashedPrice } = getOfferCardPrice(offerOrTile);

      return (
        <OfferCardV2
          key={offerOrTile.loyaltyEngineId || idx}
          incentiveData={incentiveData}
          handleClick={handleClick}
          isLocked={enableGreyedOutOffers && isGreyedOutOffer(offerOrTile, userIsAuthenticated)}
          isFavoriteEnabled={enableFavoriteOffers}
          isFavorited={enableFavoriteOffers && isFavorite(offerOrTile._id || '')}
          isFavoriteLoading={isOfferLoading(offerOrTile._id || '')}
          handleToggleFavorite={toggleFavorite}
          isUpcomingOffer={offerIsUpcoming}
          slashedPrice={slashedPrice}
          discountedPrice={priceToShow}
          shouldShowPrice={shouldShowOfferCardPrice}
          isMobile={isMobile}
        />
      );
    })
    .filter((item: any) => !!item);

  const availableExternalOffers = externalOffers
    ?.map((offer: ExternalOffer, idx: number) => {
      const isOfferClosed = isExternalOfferClosed(offer, userTier, isAuthenticated());

      if (isTestOnly(offer)) {
        return null;
      }

      // discard external offers that are not favourited for "Favourites tab"
      if (activeFilter === 2 && !isFavorite(offer._id || '')) {
        return null;
      }

      const offerTier = offer.tierIncentiveList?.[0]?.tierName as LoyaltyTierKey;

      const parsedIncentiveData = parseExternalIncentiveData(offer, isOfferClosed);

      return (
        <ExternalOfferCardV2
          key={offer._id || idx}
          incentiveData={parsedIncentiveData}
          handleClick={handleClick}
          isFavoriteEnabled={enableFavoriteOffers}
          isFavorited={enableFavoriteOffers && isFavorite(offer._id || '')}
          isFavoriteLoading={isOfferLoading(offer._id || '')}
          handleToggleFavorite={toggleFavorite}
          isOfferClosed={isOfferClosed}
          offerTier={offerTier}
          isMobile={isMobile}
          shouldShowPrice={shouldShowOfferCardPrice}
        />
      );
    })
    .filter((item: any) => !!item);

  const availableChallengeIncentives = challengeIncentives
    ?.map(challengeIncentive => {
      if (activeFilter === 2 && !isFavorite(challengeIncentive._id || '')) {
        return null;
      }

      const parsedChallengeIncentive = parseIncentiveData(challengeIncentive);

      return (
        <OfferCardV2
          key={challengeIncentive.loyaltyEngineId}
          isQuestChallengeIcentive
          incentiveData={parsedChallengeIncentive}
          handleClick={handleClick}
          isFavoriteEnabled={enableFavoriteOffers}
          isFavorited={enableFavoriteOffers && isFavorite(challengeIncentive._id || '')}
          isFavoriteLoading={isOfferLoading(challengeIncentive._id ?? '')}
          handleToggleFavorite={toggleFavorite}
          isMobile={isMobile}
        ></OfferCardV2>
      );
    })
    .filter((item: any) => !!item);

  const handleBackNavigation = () => {
    navigate(state?.backRoute || `${formatMessage({ id: 'routes.loyaltyHome' })}/${tabPath}`);
  };

  const offersAvailable = Boolean(
    challengeIncentives?.length || offersAndTiles?.length || availableExternalOffers?.length
  );
  const areAllOffersLoading = questsLoading || offersLoading || externalOffersLoading;

  const forYouOffersAvailable = availableChallengeIncentives?.length || offersAndTiles?.length;
  const forYouOffersLoading = questsLoading || offersLoading;

  const favouritesAvailable = Boolean(
    availableChallengeIncentives?.length ||
      offersAndTiles?.length ||
      availableExternalOffers?.length
  );

  const signUp = () => {
    navigate(routes.signUp);
  };

  const seeAllOffers = () => {
    setActiveFilter(0);
  };

  const seeMenu = () => {
    navigate(routes.menu);
  };

  const renderAllOffers = () => {
    if (!areAllOffersLoading && !offersAvailable) {
      return <EmptyOffersView filterTab={activeFilter} isEmpty={true} ctaAction={seeMenu} />;
    }

    return (
      <OfferGrid
        cardWidth={cardWidth}
        isMobile={isMobile}
        shouldShowOfferCardPrice={shouldShowOfferCardPrice}
      >
        {areAllOffersLoading ? (
          <OffersCardV2Skeleton />
        ) : (
          <>
            {availableChallengeIncentives}
            {offersAndTiles}
            {availableExternalOffers}
          </>
        )}
      </OfferGrid>
    );
  };

  const renderForYouOffers = () => {
    if ((!forYouOffersLoading && !forYouOffersAvailable) || !userIsAuthenticated) {
      return (
        <EmptyOffersView
          filterTab={activeFilter}
          isEmpty={!forYouOffersAvailable}
          isLoggedOut={!userIsAuthenticated}
          ctaAction={!userIsAuthenticated ? signUp : seeAllOffers}
        />
      );
    }

    return (
      <OfferGrid
        cardWidth={cardWidth}
        isMobile={isMobile}
        shouldShowOfferCardPrice={shouldShowOfferCardPrice}
      >
        {forYouOffersLoading ? (
          <OffersCardV2Skeleton />
        ) : (
          <>
            {availableChallengeIncentives}
            {offersAndTiles}
          </>
        )}
      </OfferGrid>
    );
  };

  const renderFavoriteOffers = () => {
    if ((!areAllOffersLoading && !favouritesAvailable) || !userIsAuthenticated) {
      return (
        <EmptyOffersView
          filterTab={activeFilter}
          isEmpty={!favouritesAvailable}
          isLoggedOut={!userIsAuthenticated}
          ctaAction={!userIsAuthenticated ? signUp : seeAllOffers}
        />
      );
    }

    return (
      <OfferGrid
        cardWidth={cardWidth}
        isMobile={isMobile}
        shouldShowOfferCardPrice={shouldShowOfferCardPrice}
      >
        {areAllOffersLoading ? (
          <OffersCardV2Skeleton />
        ) : (
          <>
            {availableChallengeIncentives}
            {offersAndTiles}
            {availableExternalOffers}
          </>
        )}
      </OfferGrid>
    );
  };

  const renderDeliveryAndPickupOffers = () => {
    if ((!areAllOffersLoading && !offersAvailable) || !offersAndTiles.length) {
      return <EmptyOffersView filterTab={activeFilter} isEmpty={true} ctaAction={seeAllOffers} />;
    }

    return (
      <OfferGrid
        cardWidth={cardWidth}
        isMobile={isMobile}
        shouldShowOfferCardPrice={shouldShowOfferCardPrice}
      >
        {areAllOffersLoading ? <OffersCardV2Skeleton /> : <>{offersAndTiles}</>}
      </OfferGrid>
    );
  };

  const renderExternalOffers = () => {
    if (!externalOffersLoading && !availableExternalOffers?.length) {
      return <EmptyOffersView filterTab={activeFilter} isEmpty={true} ctaAction={seeAllOffers} />;
    }

    return (
      <OfferGrid
        cardWidth={cardWidth}
        isMobile={isMobile}
        shouldShowOfferCardPrice={shouldShowOfferCardPrice}
      >
        {externalOffersLoading ? <OffersCardV2Skeleton /> : <>{availableExternalOffers}</>}
      </OfferGrid>
    );
  };

  return (
    <>
      <LoyaltyOffersGridContainer ref={ref} isLoggedIn={userIsAuthenticated}>
        <SkeletonTheme baseColor="#0000000a" highlightColor="#0000001a">
          <OfferStickyContainer>
            <OfferTitleContainer>
              <OfferTitle>{formatMessage({ id: 'offers' })}</OfferTitle>
              {showRewardCarousel && (
                <LoyaltyPromoCode
                  onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
                  CTAComponent={PromoCodeCTA}
                  CTAText={formatMessage({ id: 'promoCodeCTA' })}
                />
              )}
            </OfferTitleContainer>
            {showFilters && ((activeFilter === 0 && offersAvailable) || activeFilter !== 0) && (
              <FilterContainer>
                <FilterBarWrapper>
                  <ResponsiveCarousel
                    isV2={true}
                    activeFilter={activeFilter}
                    hideScrollBar
                    bgColor="transparent"
                  >
                    {availableFilters
                      .filter(tab => tab.shouldExist)
                      .map(filter => {
                        return (
                          <StyledButton
                            key={filter.id}
                            onClick={e => {
                              e.preventDefault();
                              if (filter.id !== 0) {
                                const tabName = tabNames[filter.id];
                                cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
                                  name: tabName,
                                  ...restaurantLogAttributesRef.current,
                                });
                              }
                              setActiveFilter(filter.id);
                            }}
                            isClicked={activeFilter === filter.id}
                          >
                            {filter.label}
                          </StyledButton>
                        );
                      })}
                  </ResponsiveCarousel>
                </FilterBarWrapper>
                {!showRewardCarousel && !isMobile && (
                  <LoyaltyPromoCode
                    onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
                    CTAComponent={PromoCodeCTA}
                    CTAText={formatMessage({ id: 'promoCodeCTA' })}
                  />
                )}
              </FilterContainer>
            )}
          </OfferStickyContainer>

          {!showRewardCarousel && isMobile && (
            <LoyaltyPromoCode
              onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
              CTAComponent={PromoCodeButtonBar}
              CTAText={
                <>
                  <div className="title">
                    <Icon
                      icon="offer"
                      color="icon-default"
                      width="15px"
                      height="15px"
                      aria-hidden
                    />
                    <PromoCodeButtonTitle>
                      {formatMessage({ id: 'promoCodeCTA' })}
                    </PromoCodeButtonTitle>
                  </div>
                  <AddPromoIcon>
                    <Icon icon="add" color="white" aria-hidden width="16px" height="16px" />
                  </AddPromoIcon>
                </>
              }
            />
          )}
          {activeFilter === 0
            ? renderAllOffers()
            : activeFilter === 1
              ? renderForYouOffers()
              : activeFilter === 2
                ? renderFavoriteOffers()
                : activeFilter === 3 || activeFilter === 4
                  ? renderDeliveryAndPickupOffers()
                  : activeFilter === 5
                    ? renderExternalOffers()
                    : null}

          {enableInvitationCode && loyaltyUser && (
            <ModalQuestUnlockedIncentive
              onChangeUnlockedIncentivesModalVisibilityStatus={
                onChangeUnlockedIncentivesModalVisibilityStatus
              }
              unlockedIncentivesModalVisibilityStatus={unlockedIncentivesModalVisibilityStatus}
            />
          )}
        </SkeletonTheme>
        {selectedIncentive && !isExternalOffer && (
          <StyledV2Modal
            className="offer-detail-v2-modal"
            height="90vh"
            size={ModalSize.REGULAR}
            allowsDismiss={false}
            onDismiss={handleBackNavigation}
            backgroundColor="white"
            eventData={{
              modalAppearanceEventMessage: 'Show offer details',
            }}
          >
            <IncentiveDetailsMemo
              challengeIncentiveSelected={challengeIncentiveSelected}
              engineIncentivesMap={{}}
              selectedIncentive={selectedIncentive}
              isV2={true}
              onGoBack={handleBackNavigation}
            />
          </StyledV2Modal>
        )}
        {selectedIncentive && isExternalOffer && (
          <StyledExternalModal
            className="external-offer-detail-v2-modal"
            height="90vh"
            size={ModalSize.REGULAR}
            allowsDismiss={false}
            onDismiss={handleBackNavigation}
            backgroundColor="white"
            eventData={{
              modalAppearanceEventMessage: 'Show offer details',
            }}
          >
            <ExternalIncentiveDetailsMemo
              selectedIncentive={selectedIncentive}
              isV2={true}
              onGoBack={handleBackNavigation}
            />
          </StyledExternalModal>
        )}
      </LoyaltyOffersGridContainer>
    </>
  );
};
